import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Form, message, Space, Table, TableProps } from "antd";
import { useEffect, useState } from "react";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import VocsModal from "src/components/Modal";
import { CommonError, CommonSuccess } from "src/entity/common/menus-enum.enum";
import { initTankEmissionControl, SettingEmissionControlTank, Filter } from "src/entity/settings/tank";
import { createTankEmissionControl, deleteTankEmissionControl, updateTankEmissionControl } from "src/services/settings.service";
import { tankStore } from "src/stores/tank-store";
import { componentPalette } from "src/theme/component-palette";
import { useShallow } from "zustand/react/shallow";
import TankEmissionControlModal from "./TankEmissionControlModal";
import axios from "axios";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "../PermissionCheck/PermissionCheck";
import { calculateReportVocEmissionMasterChange } from "src/services/tank.service";

type OnChange = NonNullable<TableProps<any>["onChange"]>;
type Filters = Parameters<OnChange>[1];

const TankEmissionControlTable = () => {
  const [mode, setMode] = useState<"insert" | "update">("insert");
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState<Filters>({});
  const [emissionId, setEmissionId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [current, setCurrent] = useState(1);
  const pageSize = 10;
  const [form] = Form.useForm();
  const [isDuplicate, setIsDuplicate] = useState<boolean>(false);
  const { permissions } = useNavigationMenu();

  const tankEmissionControl = tankStore(useShallow((state) => state.tankEmissionControl));
  const totalTankEmissionControl = tankStore(useShallow((state) => state.totalTankEmissionControl));
  const getAllTankEmissionControl = tankStore(useShallow((state) => state.getTankEmissionControl));

  const filterTankEmissionControl = tankStore(useShallow((state) => state.filterTankEmissionControl));
  const getFilterTankEmissionControl = tankStore(useShallow((state) => state.getFilterTankEmissionControl));

  const onOpenModal = (mode: "insert" | "update", data: SettingEmissionControlTank) => {
    // console.log("onOpenModal", data);
    setMode(mode);
    setShowModal(true);
    setEmissionId(data._id);
    form.setFieldsValue(data);
  };

  const onCloseModal = () => {
    setShowModal(false);
    setIsDuplicate(false);
    const formValue = form.getFieldsValue();
    if (formValue) {
      form.setFields(
        Object.keys(formValue).map((field) => ({
          name: field,
          errors: [],
        })),
      );
    }
  };

  const onSave = async (mode: "insert" | "update", data: SettingEmissionControlTank) => {
    try {
      let response;
      setLoading(true);
      if (mode == "insert") {
        response = await createTankEmissionControl(data);
      } else {
        if (emissionId) {
          data = {
            ...data,
            _id: emissionId,
          };
          response = await updateTankEmissionControl(data);
        }
      }
      if (response?.data.code === 200) {
        setLoading(false);
        setShowModal(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.SAVED_SUCCESSFULL,
        });
        fetchTankEmissionControl();
        getFilterTankEmissionControl();
        processCalculateReport(data._id);
      } else {
        setLoading(false);
        setIsDuplicate(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 409) {
          setIsDuplicate(true);
        } else {
          setIsDuplicate(false);
        }
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const processCalculateReport = async (id: string) => {
    try {
      setLoading(true);
      await calculateReportVocEmissionMasterChange(id);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onTableChange: TableProps<SettingEmissionControlTank>["onChange"] = (pagination: any, filters: any) => {
    let current = pagination.current;
    setFilter(filters);
    setCurrent(current);
  };

  const mapFilter = (key: string): Filter[] | [] => {
    const resultFilter = filterTankEmissionControl.find((item) => item.key === key)?.filter;
    if (resultFilter) {
      const filter: Filter[] = resultFilter?.map((value: any) => {
        let text = value;
        return {
          text,
          value: value,
        };
      });
      return filter;
    } else {
      return [];
    }
  };

  const showDeleteConfirm = async (value: SettingEmissionControlTank) => {
    const { _id } = value;
    VocsModal.delete({
      onOk: async () => {
        if (_id) {
          handleDelete(_id);
        }
      },
    });
  };

  const calPagination = (current?: number) => {
    if (current) {
      return (current - 1) * 10;
    }
  };

  const createParamFilter = () => {
    let queryParam = "";
    let offset = 0;
    let limit = pageSize;

    if (current) {
      let resultCalOffset = calPagination(current);
      if (resultCalOffset) {
        offset = resultCalOffset;
      }
      queryParam += `&offset=${offset}`;
    }
    if (limit) {
      queryParam += `&limit=${limit}`;
    }

    if (filter) {
      const str = Object.entries(filter)
        .filter(([, value]) => value)
        .map(([key, value]) => `&${key}=[${value?.join(",")}]`)
        .join("");
      queryParam += str;
    }

    return queryParam;
  };

  const fetchTankEmissionControl = async () => {
    const query = createParamFilter();
    setLoading(true);
    await getAllTankEmissionControl(query);
    setLoading(false);
  };

  const handleDelete = async (_id: string) => {
    try {
      setLoading(true);
      const response = await deleteTankEmissionControl(_id);
      if (response.data.code === 200) {
        setLoading(false);
        messageApi.open({
          type: "success",
          content: CommonSuccess.DELETE_SUCCESSFULL,
        });
        fetchTankEmissionControl();
      } else {
        setLoading(false);
        messageApi.open({
          type: "error",
          content: CommonError.SOMETHING_WRONG,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await fetchTankEmissionControl();
      await getFilterTankEmissionControl();
      setLoading(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    fetchTankEmissionControl();
  }, [current, filter]);

  const columns: TableProps<SettingEmissionControlTank>["columns"] = [
    {
      title: "No.",
      align: "center",
      render: (_, __, index) => <div>{index + 1 + (current - 1) * pageSize}</div>,
    },
    {
      title: "Emission Control",
      dataIndex: "emissionControl",
      filters: mapFilter("emissionControl"),
    },
    {
      title: "Efficiency (%)",
      dataIndex: "efficiency",
      filters: mapFilter("efficiency"),
    },
    {
      title: "Remark",
      dataIndex: "remark",
    },
    {
      title: "Action",
      key: "action",
      render: (value, record) => (
        <Space>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => {
              onOpenModal("update", record);
            }}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={16} color={componentPalette.blueGrey} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            onClick={() => showDeleteConfirm(value)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={16} color={componentPalette.blueGrey} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];
  return (
    <div>
      {contextHolder}
      {form && (
        <TankEmissionControlModal
          form={form}
          open={showModal}
          mode={mode}
          loading={loading}
          isDuplicate={isDuplicate}
          onDuplicateChange={(isDup) => {
            setIsDuplicate(isDup);
          }}
          onCloseModal={onCloseModal}
          onSave={(mode, data) => onSave(mode, data)}
        />
      )}
      {canEditTable(permissions) && (
        <div style={{ display: "flex", justifyContent: "space-between", paddingBottom: "10px" }}>
          <span className="mt-2">{totalTankEmissionControl}</span>

          <Button
            type="primary"
            size="middle"
            onClick={() => onOpenModal("insert", initTankEmissionControl)}
            style={{
              display: "flex",
              alignItems: "center",
            }}
            disabled={!canEditTable(permissions)}
          >
            <PlusCircleOutlined />
            New
          </Button>
        </div>
      )}

      <Table
        className="w-full"
        columns={columns}
        dataSource={tankEmissionControl}
        loading={loading}
        onChange={onTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          showSizeChanger: false,
          size: "small",
          current,
          pageSize,
          total: totalTankEmissionControl,
        }}
      />
    </div>
  );
};

export default TankEmissionControlTable;
